import { Vue, Component, Ref, Prop, Emit, Provide } from 'vue-property-decorator'
import { ConfigurationOntologyTextTemplate, initialTextTemplatesModalState, TextTemplatesModalState, TextTemplateTabIndex } from '@/modules/configurationModule/detail/templates/text-templates/components/text-templates-modal/text-templates-modal.models'
import TextTemplatesModalBasicForm from '@/modules/configurationModule/detail/templates/text-templates/components/text-templates-modal-basic-form/text-templates-modal-basic-form.vue'
import ConfigurationOntologies from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.vue'
import { cloneDeep, uniqueId } from 'lodash'
import { TextTemplateRelationShipOntologyResponseList, ConfigurationTextTemplateResponse, TextTemplatesRelationShipOntologyCreateRequest } from '@/modules/configurationModule/detail/templates/text-templates/services/text-templates.dto'
import { textTemplatesConfigurationOntologyRelationshipServices } from '@/modules/configurationModule/detail/templates/text-templates/services/text-templates-relationship-ontology.services'
import { ConfigurationOntologiesState } from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.model'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { GridUtils } from '@/shared/util/grid-utils'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { PromiseUtils } from '@/shared/util/promise-utils'

@Component({
    components: {
        TextTemplatesModalBasicForm,
        ConfigurationOntologies
    }
})
export default class TextTemplatesModal extends Vue {
    @Ref() readonly textTemplatesModal!: any
    @Prop(String) readonly actionTitle!: string
    @Prop(Number) readonly labId!: number
    @Prop() readonly broadcastChannelPostMessageFunction!: Function
    @Emit()
    update(textTemplateModal: ConfigurationTextTemplateResponse[]): ConfigurationTextTemplateResponse[] {
        return textTemplateModal
    }
    @Emit()
    create(textTemplateModal: ConfigurationTextTemplateResponse) {
        return textTemplateModal
    }
    @Provide() externalValidator = this.$validator

    state: TextTemplatesModalState
    constructor() {
        super()
        this.state = initialTextTemplatesModalState()
    }

    async getOntologies() {
        const filterModel: CommonFilterModel = {
            'textTemplate.id': GridUtils.getNumberFilterModel('equals', this.state.selectedRow?.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response =
            await PromiseUtils.toCallUnique(
                textTemplatesConfigurationOntologyRelationshipServices.getTextTemplatesRelationshipOntologies(filterModel, params, this.labId))
        if (response?.content) {
            this.state.ontologies = response.content.map((ontology: TextTemplateRelationShipOntologyResponseList) => {
                const rowOntology: ConfigurationOntologyTextTemplate = {
                    id: ontology.id,
                    sampleType: ontology.sampleType,
                    topographicCode: ontology.topographicCode,
                    mostLikelyMorphologicCode: ontology.mostLikelyMorphologicCode,
                    procedure: ontology.procedure,
                    templateId: ontology.textTemplate.id!
                }
                return rowOntology
            })
        }
    }

    async show(selectedRow: ConfigurationTextTemplateResponse): Promise<void> {
        this.state = initialTextTemplatesModalState()
        await this.$validator.reset()
        if (selectedRow) {
            await this.setSelectedRow(selectedRow)
        }
        this.textTemplatesModal.show()
        this.forceRenderer()
    }

    hide() { this.textTemplatesModal.hide() }

    forceRenderer() {
        this.state.modalUID = uniqueId()
    }

    spreadEventTemplate(template: ConfigurationTextTemplateResponse) {
        if (template?.id) {
            this.update([template])
            return
        }
        this.create(template)
    }

    async setSelectedRow(selectedRow: ConfigurationTextTemplateResponse) {
        this.state = {
            ...this.state,
            selectedRow: { ...cloneDeep(selectedRow) }
        }
        await this.getOntologies()
    }

    updateRow(selectedRow: ConfigurationTextTemplateResponse) {
        this.setSelectedRow(selectedRow)
        this.$nextTick(() => {
            this.state = {
                ...this.state,
                activeTabIndex: TextTemplateTabIndex.ONTOLOGIES
            }
        })
    }

    async createOntology(ontology: ConfigurationOntologiesState) {
        this.$_updateLoading(true)
        const createRelationshipOntology: TextTemplatesRelationShipOntologyCreateRequest = {
            mostLikelyMorphologicCodeId: ontology?.mostLikelyMorphologicCode?.id,
            procedureId: ontology?.procedure?.id,
            sampleTypeId: ontology?.sampleType?.id,
            topographicCodeId: ontology?.topographicCode?.id,
            textTemplateId: this.state.selectedRow.id
        }
        const [error] = await this.$_toCall(textTemplatesConfigurationOntologyRelationshipServices.createTextTemplatesRelationshipOntologies(createRelationshipOntology, this.labId), true)
        this.$_updateLoading(false)
        if (error) {
            return
        }
        await this.getOntologies()
        if (this.broadcastChannelPostMessageFunction) {
            this.broadcastChannelPostMessageFunction()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
    }

    async deleteOntology(ontologyId: number) {
        this.$_updateLoading(true)
        const [error] = await this.$_toCall(textTemplatesConfigurationOntologyRelationshipServices.deleteTextTemplatesRelationshipOntologies(ontologyId, this.labId), true)
        this.$_updateLoading(false)
        if (error) {
            return
        }
        await this.getOntologies()
        if (this.broadcastChannelPostMessageFunction) {
            this.broadcastChannelPostMessageFunction()
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
    }
}
